import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildEstimatedRouteLegs,
  createInitialState,
  effectiveRouteLegs,
  makeId,
  createStop,
  createTrip as createTripRecord,
  insertStop,
  moveStopToDay as moveStopToDayRecord,
  patchStop,
  sortStops,
  updateTripRecord,
} from "./domain";
import { applyRouteRefresh, refreshTripRouteLegs } from "./routeService";
import { clearState, loadState, saveState } from "./storage";
import type {
  ActivitySource,
  AccommodationNode,
  AddStopInput,
  AppScreen,
  AppState,
  CreateTripInput,
  FreeTimeBlock,
  IdeaItem,
  ItineraryStop,
  PlaceCandidate,
  Profile,
  RouteRefreshOptions,
  RouteRefreshResult,
  Trip,
  UpdateStopInput,
} from "./types";

const HISTORY_LIMIT = 30;

interface HistoryMutation {
  source: ActivitySource;
  action: string;
  detail: string;
}

export interface UndoableSnapshot {
  onboardingComplete: boolean;
  profile: Profile;
  trips: Trip[];
}

interface HistoryEntry {
  snapshot: UndoableSnapshot;
  mutation: HistoryMutation;
}

export interface AgentUndoResult {
  action: string;
  detail: string;
}

export type AgentUndoAvailability = "available" | "blocked" | "none";

export type TripPatchOperation =
  | { id: string; kind: "move"; stopId: string; targetDayId: string | null; targetPosition: number; summary: string }
  | { id: string; kind: "time"; stopId: string; startTime: string; summary: string };

export interface TripPatchPreview {
  id: string;
  tripId: string;
  dayId: string;
  title: string;
  explanation: string;
  operations: TripPatchOperation[];
  createdAt: string;
}

function makeActivity(source: ActivitySource, action: string, detail: string) {
  return {
    id: `activity_${crypto.randomUUID()}`,
    source,
    action,
    detail,
    at: new Date().toISOString(),
  };
}

function touchTrip(trip: Trip): Trip {
  return { ...trip, updatedAt: new Date().toISOString() };
}

function rebuildRoute(trip: Trip): Trip {
  const next = touchTrip(trip);
  return { ...next, routeLegs: effectiveRouteLegs(next) };
}

function stopDateForDay(trip: Trip, dayId: string | null): string | undefined {
  return dayId ? trip.days.find((day) => day.id === dayId)?.date : undefined;
}

function minutesToTime(minutes: number): string {
  const bounded = Math.min(23 * 60 + 59, Math.max(0, Math.round(minutes)));
  return `${String(Math.floor(bounded / 60)).padStart(2, "0")}:${String(bounded % 60).padStart(2, "0")}`;
}

function timeToMinutes(value: string): number | null {
  if (!value) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

function assertAgentCanEditStop(trip: Trip, stop: ItineraryStop, patch?: UpdateStopInput): void {
  const day = stop.dayId ? trip.days.find((candidate) => candidate.id === stop.dayId) : undefined;
  if (day?.locked) throw new Error("This day is locked. Ask the traveler before changing it.");
  if (stop.locks.reservation) throw new Error("This reservation is locked. Ask the traveler before changing it.");
  if (patch && stop.locks.place && (patch.name !== undefined || patch.address !== undefined || patch.category !== undefined || patch.placeRef !== undefined)) {
    throw new Error("This place is locked. Ask the traveler before changing it.");
  }
  if (patch && stop.locks.time && (patch.startTime !== undefined || patch.durationMinutes !== undefined || patch.travelBufferMinutes !== undefined)) {
    throw new Error("This stop time is locked. Ask the traveler before changing it.");
  }
}

function recalculateStopsForDay(trip: Trip, dayId: string, reorder: boolean): ItineraryStop[] {
  const dayStops = trip.stops.filter((stop) => stop.dayId === dayId);
  const ordered = reorder
    ? [...dayStops].sort((left, right) => {
        if (left.startTime && right.startTime) return left.startTime.localeCompare(right.startTime) || left.order - right.order;
        if (left.startTime) return -1;
        if (right.startTime) return 1;
        return left.order - right.order;
      })
    : sortStops(dayStops);
  const reindexed = ordered.map((stop, index) => ({ ...stop, order: index + 1 }));
  const temporary = { ...trip, stops: [...trip.stops.filter((stop) => stop.dayId !== dayId), ...reindexed] };
  const legByFrom = new Map(effectiveRouteLegs(temporary).filter((leg) => leg.dayId === dayId).map((leg) => [leg.fromStopId, leg]));
  let cursor = timeToMinutes(reindexed.find((stop) => stop.startTime)?.startTime ?? "") ?? 9 * 60;
  const recalculated = reindexed.map((stop) => {
    const locked = stop.locks.time || stop.locks.reservation;
    const lockedStart = locked ? timeToMinutes(stop.startTime) : null;
    const start = lockedStart ?? cursor;
    const next = { ...stop, startTime: minutesToTime(start), updatedAt: new Date().toISOString() };
    cursor = start + stop.durationMinutes + (legByFrom.get(stop.id)?.durationMinutes ?? 0);
    return next;
  });
  const replacements = new Map(recalculated.map((stop) => [stop.id, stop]));
  return trip.stops.map((stop) => replacements.get(stop.id) ?? stop);
}

export function captureUndoableState(state: AppState): UndoableSnapshot {
  return {
    onboardingComplete: state.onboardingComplete,
    profile: state.profile,
    trips: state.trips,
  };
}

export function restoreUndoableState(current: AppState, snapshot: UndoableSnapshot): AppState {
  const activeTripExists = snapshot.trips.some((trip) => trip.id === current.activeTripId);
  return {
    ...current,
    onboardingComplete: snapshot.onboardingComplete,
    profile: snapshot.profile,
    trips: snapshot.trips,
    activeTripId: activeTripExists ? current.activeTripId : null,
    screen: activeTripExists ? current.screen : "trips",
  };
}

export interface RoamController {
  state: AppState;
  canUndo: boolean;
  canRedo: boolean;
  canAgentUndo: boolean;
  pendingPatch: TripPatchPreview | null;
  completeOnboarding: (profile: Profile) => void;
  updateProfile: (profile: Profile) => void;
  createTrip: (input: CreateTripInput, source?: ActivitySource) => Trip;
  updateTrip: (tripId: string, input: Partial<CreateTripInput>, source?: ActivitySource) => Trip;
  deleteTrip: (tripId: string) => void;
  openTrip: (tripId: string, source?: ActivitySource) => Trip;
  setScreen: (screen: AppScreen) => void;
  addStop: (tripId: string, input: AddStopInput, source?: ActivitySource) => ItineraryStop;
  updateStop: (
    tripId: string,
    stopId: string,
    input: UpdateStopInput,
    source?: ActivitySource,
  ) => ItineraryStop;
  removeStop: (tripId: string, stopId: string) => void;
  moveStop: (tripId: string, stopId: string, targetDayId: string | null, targetPosition: number, source?: ActivitySource) => void;
  moveStopWithinDay: (tripId: string, stopId: string, dayPosition: number) => void;
  recalculateDayTimes: (tripId: string, dayId: string, source?: ActivitySource) => void;
  fixDay: (tripId: string, dayId: string) => void;
  setStopLocks: (tripId: string, stopId: string, locks: ItineraryStop["locks"]) => void;
  setDayLocked: (tripId: string, dayId: string, locked: boolean) => void;
  addFreeTime: (tripId: string, dayId: string | null, durationMinutes: number, startTime?: string, notes?: string) => FreeTimeBlock;
  removeFreeTime: (tripId: string, blockId: string) => void;
  setAccommodation: (tripId: string, dayId: string, accommodation?: AccommodationNode) => void;
  saveIdea: (tripId: string, place: PlaceCandidate) => IdeaItem;
  removeIdea: (tripId: string, ideaId: string) => void;
  proposeRelaxedDay: (tripId: string, dayId: string) => TripPatchPreview;
  applyPendingPatch: (operationIds?: string[]) => void;
  rejectPendingPatch: () => void;
  undo: () => void;
  redo: () => void;
  getAgentUndoAvailability: () => AgentUndoAvailability;
  undoLastAgentChange: () => AgentUndoResult;
  refreshRoutes: (tripId: string, options?: RouteRefreshOptions) => Promise<RouteRefreshResult>;
  replaceState: (state: AppState) => void;
  resetAll: () => void;
  getState: () => AppState;
}

export function useRoam(): RoamController {
  const [state, setState] = useState<AppState>(() => loadState());
  const [past, setPast] = useState<HistoryEntry[]>([]);
  const [future, setFuture] = useState<HistoryEntry[]>([]);
  const [pendingPatch, setPendingPatch] = useState<TripPatchPreview | null>(null);
  const stateRef = useRef(state);
  const pastRef = useRef(past);
  const futureRef = useRef(future);
  const routeAbortRef = useRef(new Map<string, AbortController>());

  useEffect(() => {
    stateRef.current = state;
    saveState(state);
  }, [state]);

  useEffect(() => () => {
    for (const controller of routeAbortRef.current.values()) controller.abort();
    routeAbortRef.current.clear();
  }, []);

  const commit = useCallback(
    (
      mutate: (current: AppState) => AppState,
      activity: HistoryMutation,
      recordHistory = true,
    ): AppState => {
      const current = stateRef.current;
      let next = mutate(current);
      next = {
        ...next,
        activity: [makeActivity(activity.source, activity.action, activity.detail), ...next.activity].slice(
          0,
          40,
        ),
      };
      if (recordHistory) {
        const nextPast = [
          ...pastRef.current.slice(-(HISTORY_LIMIT - 1)),
          { snapshot: captureUndoableState(current), mutation: activity },
        ];
        pastRef.current = nextPast;
        futureRef.current = [];
        setPast(nextPast);
        setFuture([]);
      }
      stateRef.current = next;
      setState(next);
      return next;
    },
    [],
  );

  const completeOnboarding = useCallback(
    (profile: Profile) => {
      commit(
        (current) => ({ ...current, profile, onboardingComplete: true, screen: "trips" }),
        { source: "human", action: "Profile ready", detail: `Travel preferences saved for ${profile.name}.` },
      );
    },
    [commit],
  );

  const updateProfile = useCallback(
    (profile: Profile) => {
      commit((current) => ({
        ...current,
        profile,
        trips: profile.onlineRoutingEnabled === true
          ? current.trips
          : current.trips.map((trip) => ({ ...trip, routeLegs: buildEstimatedRouteLegs(trip) })),
      }), {
        source: "human",
        action: "Profile updated",
        detail: "Travel preferences changed.",
      });
    },
    [commit],
  );

  const refreshRoutes = useCallback(
    async (tripId: string, options: RouteRefreshOptions = {}): Promise<RouteRefreshResult> => {
      const snapshot = stateRef.current;
      const trip = snapshot.trips.find((candidate) => candidate.id === tripId);
      if (!trip) throw new Error("Trip not found.");
      if (snapshot.profile.onlineRoutingEnabled !== true) {
        throw Object.assign(new Error("Enable real routes before sending itinerary coordinates to the route provider."), {
          code: "ROUTING_CONSENT_REQUIRED",
        });
      }
      if (options.dayId && !trip.days.some((day) => day.id === options.dayId)) throw new Error("Trip day not found.");

      const key = `${tripId}:${options.dayId ?? "all"}`;
      routeAbortRef.current.get(key)?.abort();
      const controller = new AbortController();
      const abort = () => controller.abort();
      options.signal?.addEventListener("abort", abort, { once: true });
      routeAbortRef.current.set(key, controller);
      try {
        const result = await refreshTripRouteLegs(trip, {
          ...(options.dayId ? { dayId: options.dayId } : {}),
          force: options.force,
          signal: controller.signal,
        });
        const apply = (current: AppState): AppState => ({
          ...current,
          trips: current.trips.map((candidate) => {
            if (candidate.id !== tripId) return candidate;
            return applyRouteRefresh(candidate, result.routeLegs);
          }),
        });
        if (options.source === "agent") {
          commit(apply, {
            source: "agent",
            action: "Routes refreshed",
            detail: `${result.counts.fresh} fresh, ${result.counts.cached} cached, ${result.counts.estimated} estimated, and ${result.counts.planned} planned route legs are ready.`,
          }, false);
        } else {
          const next = apply(stateRef.current);
          stateRef.current = next;
          setState(next);
        }
        return result;
      } finally {
        if (routeAbortRef.current.get(key) === controller) routeAbortRef.current.delete(key);
        options.signal?.removeEventListener("abort", abort);
      }
    },
    [commit],
  );

  const createTrip = useCallback(
    (input: CreateTripInput, source: ActivitySource = "human") => {
      const trip = createTripRecord(input, stateRef.current.profile);
      commit(
        (current) => ({
          ...current,
          trips: [trip, ...current.trips],
          activeTripId: trip.id,
          screen: "planner",
        }),
        { source, action: "Trip created", detail: `${trip.name} is ready to plan.` },
      );
      return trip;
    },
    [commit],
  );

  const updateTrip = useCallback(
    (tripId: string, input: Partial<CreateTripInput>, source: ActivitySource = "human") => {
      const currentTrip = stateRef.current.trips.find((trip) => trip.id === tripId);
      if (!currentTrip) throw new Error("Trip not found.");
      const updated = rebuildRoute(updateTripRecord(currentTrip, input));

      commit(
        (current) => ({
          ...current,
          trips: current.trips.map((trip) => (trip.id === tripId ? updated : trip)),
        }),
        { source, action: "Trip updated", detail: `${updated.name} details and day plan changed.` },
      );
      return updated;
    },
    [commit],
  );

  const deleteTrip = useCallback(
    (tripId: string) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      if (!trip) return;
      commit(
        (current) => ({
          ...current,
          trips: current.trips.filter((candidate) => candidate.id !== tripId),
          activeTripId: current.activeTripId === tripId ? null : current.activeTripId,
          screen: current.activeTripId === tripId ? "trips" : current.screen,
        }),
        { source: "human", action: "Trip deleted", detail: `${trip.name} was removed locally.` },
      );
    },
    [commit],
  );

  const openTrip = useCallback(
    (tripId: string, source: ActivitySource = "human") => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      if (!trip) throw new Error("Trip not found.");
      if (source === "agent") {
        commit(
          (current) => ({ ...current, activeTripId: tripId, screen: "planner" }),
          { source, action: "Trip opened", detail: `${trip.name} is visible in the planner.` },
          false,
        );
      } else {
        const next = { ...stateRef.current, activeTripId: tripId, screen: "planner" as const };
        stateRef.current = next;
        setState(next);
      }
      return trip;
    },
    [commit],
  );

  const setScreen = useCallback(
    (screen: AppScreen) => {
      const next = { ...stateRef.current, screen };
      stateRef.current = next;
      setState(next);
    },
    [],
  );

  const addStop = useCallback(
    (tripId: string, input: AddStopInput, source: ActivitySource = "human") => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      if (!trip) throw new Error("Trip not found.");
      const duplicate = trip.stops.find((candidate) =>
        (input.placeRef && candidate.placeRef?.id === input.placeRef.id)
        || (candidate.name.trim().toLocaleLowerCase() === input.name.trim().toLocaleLowerCase()
          && Math.abs(candidate.lat - input.lat) < 0.0001
          && Math.abs(candidate.lon - input.lon) < 0.0001),
      );
      if (duplicate) throw new Error(`${duplicate.name} is already in this trip.`);
      const stop = createStop(trip, input);
      commit(
        (current) => ({
          ...current,
          activeTripId: tripId,
          screen: "planner",
          trips: current.trips.map((candidate) =>
            candidate.id === tripId
              ? rebuildRoute({ ...candidate, stops: insertStop(candidate.stops, stop) })
              : candidate,
          ),
        }),
        { source, action: "Stop added", detail: `${stop.name} was added to ${trip.name}.` },
      );
      return stop;
    },
    [commit],
  );

  const updateStop = useCallback(
    (tripId: string, stopId: string, input: UpdateStopInput, source: ActivitySource = "human") => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const stop = trip?.stops.find((candidate) => candidate.id === stopId);
      if (!trip || !stop) throw new Error("Stop not found.");
      if (source === "agent") assertAgentCanEditStop(trip, stop, input);
      const updated = patchStop(trip, stop, input);
      commit(
        (current) => ({
          ...current,
          activeTripId: tripId,
          screen: "planner",
          trips: current.trips.map((candidate) =>
            candidate.id === tripId
              ? rebuildRoute({
                  ...candidate,
                  stops: candidate.stops.map((item) => (item.id === stopId ? updated : item)),
                })
              : candidate,
          ),
        }),
        { source, action: "Stop updated", detail: `${updated.name} now reflects the latest plan.` },
      );
      return updated;
    },
    [commit],
  );

  const removeStop = useCallback(
    (tripId: string, stopId: string) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const stop = trip?.stops.find((candidate) => candidate.id === stopId);
      if (!trip || !stop) return;
      commit(
        (current) => ({
          ...current,
          trips: current.trips.map((candidate) =>
            candidate.id === tripId
              ? rebuildRoute({ ...candidate, stops: candidate.stops.filter((item) => item.id !== stopId) })
              : candidate,
          ),
        }),
        { source: "human", action: "Stop removed", detail: `${stop.name} was removed locally.` },
      );
    },
    [commit],
  );

  const moveStop = useCallback(
    (tripId: string, stopId: string, targetDayId: string | null, targetPosition: number, source: ActivitySource = "human") => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      if (!trip) throw new Error("Trip not found.");
      if (targetDayId !== null && !trip.days.some((day) => day.id === targetDayId)) throw new Error("Target day does not belong to this trip.");
      const stop = trip.stops.find((candidate) => candidate.id === stopId);
      if (!stop) throw new Error("Stop not found.");
      if (source === "agent") {
        assertAgentCanEditStop(trip, stop);
        if (stop.locks.day) throw new Error("This stop's day is locked. Ask the traveler before moving it.");
        const targetDay = targetDayId ? trip.days.find((day) => day.id === targetDayId) : undefined;
        if (targetDay?.locked) throw new Error("The target day is locked. Ask the traveler before moving a stop there.");
      }
      const movedStops = moveStopToDayRecord(trip.stops, stopId, targetDayId, targetPosition);
      const nextStops = movedStops.map((candidate) => candidate.id === stopId
        ? { ...candidate, date: stopDateForDay(trip, targetDayId) }
        : candidate);
      commit(
        (current) => ({
          ...current,
          activeTripId: tripId,
          screen: "planner",
          trips: current.trips.map((candidate) =>
            candidate.id === tripId ? rebuildRoute({ ...candidate, stops: nextStops }) : candidate,
          ),
        }),
        {
          source,
          action: "Route reordered",
          detail: `${stop.name} moved to ${targetDayId ? `day ${trip.days.find((day) => day.id === targetDayId)!.dayIndex}` : "Unscheduled"}, position ${targetPosition}.`,
        },
      );
    },
    [commit],
  );

  const moveStopWithinDay = useCallback(
    (tripId: string, stopId: string, dayPosition: number) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      if (!trip) throw new Error("Trip not found.");
      const stop = trip.stops.find((candidate) => candidate.id === stopId);
      if (!stop) throw new Error("Stop not found.");
      moveStop(tripId, stopId, stop.dayId, dayPosition, "human");
    },
    [moveStop],
  );

  const recalculateDayTimes = useCallback(
    (tripId: string, dayId: string, source: ActivitySource = "human") => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const day = trip?.days.find((candidate) => candidate.id === dayId);
      if (!trip || !day) throw new Error("Trip day not found.");
      if (source === "agent" && day.locked) throw new Error("This day is locked. Ask the traveler before changing its schedule.");
      const nextStops = recalculateStopsForDay(trip, dayId, false);
      commit(
        (current) => ({
          ...current,
          activeTripId: tripId,
          screen: "planner",
          trips: current.trips.map((candidate) => candidate.id === tripId
            ? rebuildRoute({ ...candidate, stops: nextStops })
            : candidate),
        }),
        { source, action: "Times recalculated", detail: `Day ${day.dayIndex} now follows route order and travel estimates.` },
      );
    },
    [commit],
  );

  const fixDay = useCallback(
    (tripId: string, dayId: string) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const day = trip?.days.find((candidate) => candidate.id === dayId);
      if (!trip || !day) throw new Error("Trip day not found.");
      const nextStops = recalculateStopsForDay(trip, dayId, true);
      commit(
        (current) => ({
          ...current,
          activeTripId: tripId,
          screen: "planner",
          trips: current.trips.map((candidate) => candidate.id === tripId
            ? rebuildRoute({ ...candidate, stops: nextStops })
            : candidate),
        }),
        { source: "human", action: "Day fixed", detail: `Day ${day.dayIndex} was reordered and timed around travel estimates.` },
      );
    },
    [commit],
  );

  const setStopLocks = useCallback(
    (tripId: string, stopId: string, locks: ItineraryStop["locks"]) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const stop = trip?.stops.find((candidate) => candidate.id === stopId);
      if (!trip || !stop) throw new Error("Stop not found.");
      const updated = { ...stop, locks: { ...locks }, updatedAt: new Date().toISOString() };
      commit(
        (current) => ({ ...current, trips: current.trips.map((candidate) => candidate.id === tripId
          ? touchTrip({ ...candidate, stops: candidate.stops.map((item) => item.id === stopId ? updated : item) })
          : candidate) }),
        { source: "human", action: "Stop locks updated", detail: `${stop.name} planning locks changed.` },
      );
    },
    [commit],
  );

  const setDayLocked = useCallback(
    (tripId: string, dayId: string, locked: boolean) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const day = trip?.days.find((candidate) => candidate.id === dayId);
      if (!trip || !day) throw new Error("Trip day not found.");
      commit(
        (current) => ({ ...current, trips: current.trips.map((candidate) => candidate.id === tripId
          ? touchTrip({ ...candidate, days: candidate.days.map((item) => item.id === dayId ? { ...item, locked } : item) })
          : candidate) }),
        { source: "human", action: locked ? "Day locked" : "Day unlocked", detail: `Day ${day.dayIndex} ${locked ? "will stay stable during agent edits" : "can be adjusted by the agent again"}.` },
      );
    },
    [commit],
  );

  const addFreeTime = useCallback(
    (tripId: string, dayId: string | null, durationMinutes: number, startTime?: string, notes?: string) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      if (!trip) throw new Error("Trip not found.");
      if (dayId !== null && !trip.days.some((day) => day.id === dayId)) throw new Error("Trip day not found.");
      if (!Number.isInteger(durationMinutes) || durationMinutes < 15 || durationMinutes > 720) throw new Error("Free time must be between 15 and 720 minutes.");
      if (startTime && timeToMinutes(startTime) === null) throw new Error("Free time must use HH:MM.");
      const block: FreeTimeBlock = { id: makeId("free"), dayId, durationMinutes, ...(startTime ? { startTime } : {}), ...(notes?.trim() ? { notes: notes.trim() } : {}) };
      commit(
        (current) => ({ ...current, trips: current.trips.map((candidate) => candidate.id === tripId
          ? touchTrip({ ...candidate, freeTimeBlocks: [...candidate.freeTimeBlocks, block] })
          : candidate) }),
        { source: "human", action: "Free time kept", detail: `${durationMinutes} minutes will remain open.` },
      );
      return block;
    },
    [commit],
  );

  const removeFreeTime = useCallback(
    (tripId: string, blockId: string) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const block = trip?.freeTimeBlocks.find((candidate) => candidate.id === blockId);
      if (!trip || !block) return;
      commit(
        (current) => ({ ...current, trips: current.trips.map((candidate) => candidate.id === tripId
          ? touchTrip({ ...candidate, freeTimeBlocks: candidate.freeTimeBlocks.filter((item) => item.id !== blockId) })
          : candidate) }),
        { source: "human", action: "Free time removed", detail: "The open block was removed from the timeline." },
      );
    },
    [commit],
  );

  const setAccommodation = useCallback(
    (tripId: string, dayId: string, accommodation?: AccommodationNode) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const day = trip?.days.find((candidate) => candidate.id === dayId);
      if (!trip || !day) throw new Error("Trip day not found.");
      commit(
        (current) => ({ ...current, trips: current.trips.map((candidate) => candidate.id === tripId
          ? touchTrip({ ...candidate, days: candidate.days.map((item) => item.id === dayId ? { ...item, accommodation } : item) })
          : candidate) }),
        { source: "human", action: "Accommodation updated", detail: accommodation ? `${accommodation.name} anchors Day ${day.dayIndex}.` : `Day ${day.dayIndex} accommodation was cleared.` },
      );
    },
    [commit],
  );

  const saveIdea = useCallback(
    (tripId: string, place: PlaceCandidate) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      if (!trip) throw new Error("Trip not found.");
      if (trip.ideas.some((idea) => idea.placeRef?.id === place.id)) throw new Error(`${place.name} is already in Ideas.`);
      const idea: IdeaItem = {
        id: makeId("idea"),
        title: place.name,
        address: place.displayName,
        lat: place.lat,
        lon: place.lon,
        category: place.category,
        placeRef: { id: place.id, provider: "openstreetmap-nominatim", resultId: place.id },
        createdAt: new Date().toISOString(),
      };
      commit(
        (current) => ({ ...current, trips: current.trips.map((candidate) => candidate.id === tripId
          ? touchTrip({ ...candidate, ideas: [...candidate.ideas, idea] })
          : candidate) }),
        { source: "human", action: "Idea saved", detail: `${place.name} was saved without scheduling it.` },
      );
      return idea;
    },
    [commit],
  );

  const removeIdea = useCallback(
    (tripId: string, ideaId: string) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const idea = trip?.ideas.find((candidate) => candidate.id === ideaId);
      if (!trip || !idea) return;
      commit(
        (current) => ({ ...current, trips: current.trips.map((candidate) => candidate.id === tripId
          ? touchTrip({ ...candidate, ideas: candidate.ideas.filter((item) => item.id !== ideaId) })
          : candidate) }),
        { source: "human", action: "Idea removed", detail: `${idea.title} left the Ideas inbox.` },
      );
    },
    [commit],
  );

  const proposeRelaxedDay = useCallback(
    (tripId: string, dayId: string) => {
      const trip = stateRef.current.trips.find((candidate) => candidate.id === tripId);
      const day = trip?.days.find((candidate) => candidate.id === dayId);
      if (!trip || !day) throw new Error("Trip day not found.");
      if (day.locked) throw new Error("This day is locked. Ask the traveler before changing it.");
      const stops = sortStops(trip.stops.filter((stop) => stop.dayId === dayId));
      const operations: TripPatchOperation[] = [];
      const kept = stops.slice(0, 3);
      for (const stop of stops.slice(3)) {
        assertAgentCanEditStop(trip, stop);
        if (stop.locks.day) continue;
        operations.push({ id: makeId("patchop"), kind: "move", stopId: stop.id, targetDayId: null, targetPosition: 1, summary: `Move ${stop.name} to Unscheduled` });
      }
      let cursor = 9 * 60 + 30;
      const legByFrom = new Map(effectiveRouteLegs({ ...trip, stops: kept }).map((leg) => [leg.fromStopId, leg]));
      for (const stop of kept) {
        if (!stop.locks.time && !stop.locks.reservation) {
          const startTime = minutesToTime(cursor);
          if (startTime !== stop.startTime) operations.push({ id: makeId("patchop"), kind: "time", stopId: stop.id, startTime, summary: `Set ${stop.name} to ${startTime}` });
        } else {
          cursor = timeToMinutes(stop.startTime) ?? cursor;
        }
        cursor += stop.durationMinutes + (legByFrom.get(stop.id)?.durationMinutes ?? 0) + 30;
      }
      const preview: TripPatchPreview = {
        id: makeId("patch"),
        tripId,
        dayId,
        title: `Make Day ${day.dayIndex} more relaxed`,
        explanation: "Keep up to three anchors, add 30-minute breathing room, and leave overflow places unscheduled instead of deleting them.",
        operations,
        createdAt: new Date().toISOString(),
      };
      setPendingPatch(preview);
      commit(
        (current) => current,
        { source: "agent", action: "Patch preview ready", detail: `${preview.title}: ${operations.length} proposed change${operations.length === 1 ? "" : "s"}.` },
        false,
      );
      return preview;
    },
    [commit],
  );

  const applyPendingPatch = useCallback(
    (operationIds?: string[]) => {
      const preview = pendingPatch;
      if (!preview) throw new Error("No agent patch is waiting for review.");
      const trip = stateRef.current.trips.find((candidate) => candidate.id === preview.tripId);
      if (!trip) throw new Error("Trip not found.");
      const selected = operationIds ? new Set(operationIds) : null;
      const operations = preview.operations.filter((operation) => !selected || selected.has(operation.id));
      if (!operations.length) throw new Error("Select at least one proposed change.");
      let nextStops = trip.stops;
      const dayDates = Object.fromEntries(trip.days.map((day) => [day.id, day.date]));
      for (const operation of operations) {
        const stop = nextStops.find((candidate) => candidate.id === operation.stopId);
        if (!stop) throw new Error("A proposed stop is no longer available. Ask the agent to preview again.");
        if (operation.kind === "move") {
          assertAgentCanEditStop({ ...trip, stops: nextStops }, stop);
          if (stop.locks.day) throw new Error(`${stop.name} has a day lock and cannot be moved.`);
          const targetPosition = operation.targetDayId === null
            ? nextStops.filter((candidate) => candidate.dayId === null).length + 1
            : operation.targetPosition;
          nextStops = moveStopToDayRecord(nextStops, stop.id, operation.targetDayId, targetPosition, dayDates);
        } else {
          assertAgentCanEditStop({ ...trip, stops: nextStops }, stop, { startTime: operation.startTime });
          const updated = patchStop(trip, stop, { startTime: operation.startTime, explanation: "Moved to preserve breathing room on this day." });
          nextStops = nextStops.map((candidate) => candidate.id === stop.id ? updated : candidate);
        }
      }
      commit(
        (current) => ({ ...current, activeTripId: trip.id, screen: "planner", trips: current.trips.map((candidate) => candidate.id === trip.id ? rebuildRoute({ ...candidate, stops: nextStops }) : candidate) }),
        { source: "agent", action: "Agent patch applied", detail: `${operations.length} reviewed change${operations.length === 1 ? "" : "s"} applied to Day ${trip.days.find((day) => day.id === preview.dayId)?.dayIndex ?? ""}.` },
      );
      setPendingPatch(null);
    },
    [commit, pendingPatch],
  );

  const rejectPendingPatch = useCallback(() => {
    if (!pendingPatch) return;
    const title = pendingPatch.title;
    setPendingPatch(null);
    commit(
      (current) => current,
      { source: "human", action: "Agent patch rejected", detail: `${title} was not applied.` },
      false,
    );
  }, [commit, pendingPatch]);

  const undo = useCallback(() => {
    const previous = pastRef.current.at(-1);
    if (!previous) return;
    const current = stateRef.current;
    const nextPast = pastRef.current.slice(0, -1);
    const nextFuture = [
      { snapshot: captureUndoableState(current), mutation: previous.mutation },
      ...futureRef.current,
    ].slice(0, HISTORY_LIMIT);
    const restored = restoreUndoableState(current, previous.snapshot);
    restored.activity = [
      makeActivity("human", "Change undone", `Reversed: ${previous.mutation.detail}`),
      ...current.activity,
    ].slice(0, 40);
    pastRef.current = nextPast;
    futureRef.current = nextFuture;
    stateRef.current = restored;
    setPast(nextPast);
    setFuture(nextFuture);
    setState(restored);
  }, []);

  const redo = useCallback(() => {
    const next = futureRef.current[0];
    if (!next) return;
    const current = stateRef.current;
    const nextPast = [
      ...pastRef.current.slice(-(HISTORY_LIMIT - 1)),
      { snapshot: captureUndoableState(current), mutation: next.mutation },
    ];
    const nextFuture = futureRef.current.slice(1);
    const restored = restoreUndoableState(current, next.snapshot);
    restored.activity = [
      makeActivity("human", "Change redone", `Restored: ${next.mutation.detail}`),
      ...current.activity,
    ].slice(0, 40);
    pastRef.current = nextPast;
    futureRef.current = nextFuture;
    stateRef.current = restored;
    setPast(nextPast);
    setFuture(nextFuture);
    setState(restored);
  }, []);

  const undoLastAgentChange = useCallback((): AgentUndoResult => {
    const previous = pastRef.current.at(-1);
    if (!previous) throw new Error("No agent change is available to undo in this session.");
    if (previous.mutation.source !== "agent") {
      throw new Error("The latest change was made by you, so the agent cannot undo it.");
    }

    const current = stateRef.current;
    const nextPast = pastRef.current.slice(0, -1);
    const nextFuture = [
      { snapshot: captureUndoableState(current), mutation: previous.mutation },
      ...futureRef.current,
    ].slice(0, HISTORY_LIMIT);
    const restored = restoreUndoableState(current, previous.snapshot);
    restored.activity = [
      makeActivity("agent", "Agent change undone", `Reversed: ${previous.mutation.detail}`),
      ...current.activity,
    ].slice(0, 40);

    pastRef.current = nextPast;
    futureRef.current = nextFuture;
    stateRef.current = restored;
    setPast(nextPast);
    setFuture(nextFuture);
    setState(restored);
    return { action: previous.mutation.action, detail: previous.mutation.detail };
  }, []);

  const getAgentUndoAvailability = useCallback((): AgentUndoAvailability => {
    const latest = pastRef.current.at(-1);
    if (!latest) return "none";
    return latest.mutation.source === "agent" ? "available" : "blocked";
  }, []);

  const resetAll = useCallback(() => {
    const initial = createInitialState();
    clearState();
    stateRef.current = initial;
    pastRef.current = [];
    futureRef.current = [];
    setPast([]);
    setFuture([]);
    setPendingPatch(null);
    setState(initial);
  }, []);

  const replaceState = useCallback((next: AppState) => {
    stateRef.current = next;
    pastRef.current = [];
    futureRef.current = [];
    setPast([]);
    setFuture([]);
    setPendingPatch(null);
    setState(next);
  }, []);

  return {
    state,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    canAgentUndo: past.at(-1)?.mutation.source === "agent",
    pendingPatch,
    completeOnboarding,
    updateProfile,
    createTrip,
    updateTrip,
    deleteTrip,
    openTrip,
    setScreen,
    addStop,
    updateStop,
    removeStop,
    moveStop,
    moveStopWithinDay,
    recalculateDayTimes,
    fixDay,
    setStopLocks,
    setDayLocked,
    addFreeTime,
    removeFreeTime,
    setAccommodation,
    saveIdea,
    removeIdea,
    proposeRelaxedDay,
    applyPendingPatch,
    rejectPendingPatch,
    undo,
    redo,
    getAgentUndoAvailability,
    undoLastAgentChange,
    refreshRoutes,
    replaceState,
    resetAll,
    getState: () => stateRef.current,
  };
}
